import React, { useState, useEffect } from "react";
import Template from "../../components/Template";
import { useNavigate } from "react-router-dom";
import FormLabel from "../../components/FormLabel";
import ConfirmationModal from "../../components/ConfirmationModal";
import SuccessModal from "../../components/SuccessModal";
import AlertDialog from "../../components/AlertDialog";
import api from "../../services/api";
import usePermission from "../../hooks/usePermission";
import { formatCurrencyFromDb, maskCurrency } from "../../utils/masks";
import { parseCurrencyToFloat } from "../../utils/utils";

/** Formas de pagamento oferecidas. Campo livre no banco, lista fechada na tela. */
const FORMAS_PAGAMENTO = [
    "Boleto",
    "PIX",
    "Transferência",
    "Cartão de Crédito",
    "Cartão de Débito",
    "Dinheiro",
    "Débito Automático",
];

const CadastrarDespesa = ({ despesaId }) => {
    const navigate = useNavigate();
    const isEditMode = Boolean(despesaId);
    const podeDeletar = usePermission("EXCLUIR_DESPESA");

    const [formData, setFormData] = useState({
        id: "",
        descricao: "",
        categoriaId: "",
        valor: "",
        dataVencimento: "",
        dataPagamento: "",
        statusId: "",
        formaPagamento: "",
        documento: "",
        obraId: "",
        fornecedorId: "",
        observacao: "",
    });

    const [categorias, setCategorias] = useState([]);
    const [statusList, setStatusList] = useState([]);
    const [obras, setObras] = useState([]);
    const [fornecedores, setFornecedores] = useState([]);

    const [salvando, setSalvando] = useState(false);
    const [carregando, setCarregando] = useState(isEditMode);
    const [showConfirmDeleteRegister, setShowConfirmDeleteRegister] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [showAlertDialog, setShowAlertDialog] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [erros, setErros] = useState({});

    useEffect(() => {
        fetchCategorias();
        fetchStatus();
        fetchObras();
        fetchFornecedores();
        if (isEditMode) fetchDespesa();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditMode, despesaId]);

    const fetchDespesa = async () => {
        try {
            const { data } = await api.get(`/despesas/${despesaId}`);
            setFormData({
                id: data.id,
                descricao: data.descricao || "",
                categoriaId: data.categoria?.id || "",
                valor: data.valor ? formatCurrencyFromDb(data.valor) : "",
                dataVencimento: data.dataVencimento ? data.dataVencimento.substring(0, 10) : "",
                dataPagamento: data.dataPagamento ? data.dataPagamento.substring(0, 10) : "",
                statusId: data.status?.id || "",
                formaPagamento: data.formaPagamento || "",
                documento: data.documento || "",
                obraId: data.obra?.id || "",
                fornecedorId: data.fornecedor?.id || "",
                observacao: data.observacao || "",
            });
        } catch (error) {
            setAlertMessage("Erro ao carregar a despesa. Tente novamente.");
            setShowAlertDialog(true);
        } finally {
            setCarregando(false);
        }
    };

    const fetchCategorias = async () => {
        try {
            const { data } = await api.get("/despesas/categorias?situacao=Ativo");
            setCategorias(data);
        } catch (error) { /* a validação do submit cobre a lista vazia */ }
    };

    const fetchStatus = async () => {
        try {
            const { data } = await api.get("/statuses/byModule/nome/despesa");
            setStatusList(data);
        } catch (error) { /* idem */ }
    };

    const fetchObras = async () => {
        try {
            const { data } = await api.get("/obras/listar/todas");
            setObras(Array.isArray(data) ? data : (data.records || []));
        } catch (error) { /* obra é opcional: sem lista, o campo fica vazio */ }
    };

    const fetchFornecedores = async () => {
        try {
            const { data } = await api.get("/fornecedores/todos");
            setFornecedores(Array.isArray(data) ? data : (data.records || []));
        } catch (error) { /* fornecedor é opcional */ }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (erros[name]) setErros((prev) => ({ ...prev, [name]: null }));
    };

    const handleCurrencyChange = (e) => {
        setFormData((prev) => ({ ...prev, valor: maskCurrency(e.target.value) }));
        if (erros.valor) setErros((prev) => ({ ...prev, valor: null }));
    };

    /**
     * Valida antes de abrir a confirmação. Sem isto o usuário confirmaria o
     * salvamento para só então descobrir que faltava um campo.
     */
    const validar = () => {
        const novosErros = {};
        if (!formData.descricao.trim()) novosErros.descricao = "Informe a descrição da despesa.";
        if (!formData.categoriaId) novosErros.categoriaId = "Selecione a categoria.";
        if (!formData.statusId) novosErros.statusId = "Selecione o status.";
        if (!formData.dataVencimento) novosErros.dataVencimento = "Informe a data de vencimento.";

        const valorNumerico = parseCurrencyToFloat(formData.valor);
        if (!valorNumerico || valorNumerico <= 0) novosErros.valor = "Informe um valor maior que zero.";

        if (formData.dataPagamento && formData.dataVencimento
            && formData.dataPagamento < formData.dataVencimento) {
            // Não é erro de digitação necessariamente (adiantamento acontece),
            // mas vale avisar para o usuário confirmar que é isso mesmo.
            novosErros.dataPagamento = "Pagamento anterior ao vencimento. Confira a data.";
        }

        setErros(novosErros);
        return Object.keys(novosErros).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validar()) return;
        setShowConfirmDialog(true);
    };

    const handleConfirm = async () => {
        setShowConfirmDialog(false);
        setSalvando(true);
        try {
            const payload = {
                descricao: formData.descricao.trim(),
                categoriaId: formData.categoriaId,
                valor: parseCurrencyToFloat(formData.valor),
                dataVencimento: formData.dataVencimento,
                // String vazia (e não undefined) para o backend entender que é
                // para limpar a baixa, e não para manter a data anterior.
                dataPagamento: formData.dataPagamento || "",
                statusId: formData.statusId,
                formaPagamento: formData.formaPagamento,
                documento: formData.documento,
                obraId: formData.obraId,
                fornecedorId: formData.fornecedorId,
                observacao: formData.observacao,
            };

            if (isEditMode) {
                await api.put(`/despesas/${despesaId}`, payload);
            } else {
                await api.post(`/despesas`, payload);
            }

            setShowSuccessDialog(true);
            setTimeout(() => {
                setShowSuccessDialog(false);
                navigate("/despesas");
            }, 1500);
        } catch (error) {
            setAlertMessage(error.response?.data?.error || "Ocorreu um erro. Verifique os dados e tente novamente.");
            setShowAlertDialog(true);
        } finally {
            setSalvando(false);
        }
    };

    const handleDeleteRegister = async () => {
        try {
            await api.delete(`/despesas/${despesaId}`);
            setShowConfirmDeleteRegister(false);
            navigate("/despesas");
        } catch (error) {
            setShowConfirmDeleteRegister(false);
            setAlertMessage(error.response?.data?.error || "Erro ao remover despesa.");
            setShowAlertDialog(true);
        }
    };

    const handleBack = () => navigate("/despesas");

    const classeCampo = (campo) =>
        `pl-3 pr-3 py-2 bg-white rounded-md border ${erros[campo] ? "border-red-500" : "border-slate-300"}`;

    const MensagemErro = ({ campo }) =>
        erros[campo] ? <span className="text-red-600 text-xs">{erros[campo]}</span> : null;

    return (
        <Template>
            <form onSubmit={handleSubmit} className="w-full px-4 py-6 flex flex-col gap-8">
                <div>
                    <h1 className="text-2xl text-stone-800 font-medium">
                        {isEditMode ? "Editar Despesa" : "Lançar Despesa"}
                    </h1>
                    <p className="text-stone-500 text-sm mt-1">
                        Contas a pagar da empresa. Vincule a uma obra apenas quando a despesa
                        for daquela obra; despesa administrativa fica sem vínculo.
                    </p>
                </div>

                {carregando ? (
                    <div className="text-stone-500 text-sm">Carregando despesa...</div>
                ) : (
                    <>
                        <div className="flex flex-col gap-6">
                            <div className="text-stone-600 text-base font-medium">Informações Básicas</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                                {/* COLUNA 1 */}
                                <div className="flex flex-col gap-4">
                                    {isEditMode && (
                                        <div className="w-full flex flex-col gap-1.5">
                                            <label className="text-slate-700 text-sm font-medium leading-tight">ID</label>
                                            <input
                                                type="text"
                                                className="pl-3 pr-3 py-2 bg-gray-100 rounded-md border border-slate-300"
                                                value={formData.id}
                                                readOnly
                                            />
                                        </div>
                                    )}

                                    <div className="w-full flex flex-col gap-1.5">
                                        <FormLabel htmlFor="descricao" isRequired={true}>Descrição</FormLabel>
                                        <input
                                            id="descricao"
                                            name="descricao"
                                            type="text"
                                            placeholder="Ex.: Aluguel do escritório — setembro"
                                            className={classeCampo("descricao")}
                                            value={formData.descricao}
                                            onChange={handleInputChange}
                                        />
                                        <MensagemErro campo="descricao" />
                                    </div>

                                    <div className="w-full flex flex-col gap-1.5">
                                        <FormLabel htmlFor="categoriaId" isRequired={true}>Categoria</FormLabel>
                                        <select
                                            id="categoriaId"
                                            name="categoriaId"
                                            className={classeCampo("categoriaId")}
                                            value={formData.categoriaId}
                                            onChange={handleInputChange}
                                        >
                                            <option value="">Selecione</option>
                                            {categorias.map((c) => (
                                                <option key={c.id} value={c.id}>{c.nome}</option>
                                            ))}
                                        </select>
                                        <MensagemErro campo="categoriaId" />
                                    </div>

                                    <div className="w-full flex flex-col gap-1.5">
                                        <FormLabel htmlFor="valor" isRequired={true}>Valor</FormLabel>
                                        <input
                                            id="valor"
                                            name="valor"
                                            type="text"
                                            inputMode="decimal"
                                            placeholder="0,00"
                                            className={classeCampo("valor")}
                                            value={formData.valor}
                                            onChange={handleCurrencyChange}
                                        />
                                        <MensagemErro campo="valor" />
                                    </div>

                                    <div className="w-full flex flex-col gap-1.5">
                                        <FormLabel htmlFor="documento">Documento / Referência</FormLabel>
                                        <input
                                            id="documento"
                                            name="documento"
                                            type="text"
                                            placeholder="Nº do recibo, fatura ou contrato"
                                            className={classeCampo("documento")}
                                            value={formData.documento}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>

                                {/* COLUNA 2 */}
                                <div className="flex flex-col gap-4">
                                    <div className="w-full flex flex-col gap-1.5">
                                        <FormLabel htmlFor="dataVencimento" isRequired={true}>Data de Vencimento</FormLabel>
                                        <input
                                            id="dataVencimento"
                                            name="dataVencimento"
                                            type="date"
                                            className={classeCampo("dataVencimento")}
                                            value={formData.dataVencimento}
                                            onChange={handleInputChange}
                                        />
                                        <MensagemErro campo="dataVencimento" />
                                    </div>

                                    <div className="w-full flex flex-col gap-1.5">
                                        <FormLabel htmlFor="statusId" isRequired={true}>Status</FormLabel>
                                        <select
                                            id="statusId"
                                            name="statusId"
                                            className={classeCampo("statusId")}
                                            value={formData.statusId}
                                            onChange={handleInputChange}
                                        >
                                            <option value="">Selecione</option>
                                            {statusList.map((s) => (
                                                <option key={s.id} value={s.id}>{s.nome}</option>
                                            ))}
                                        </select>
                                        <MensagemErro campo="statusId" />
                                    </div>

                                    <div className="w-full flex flex-col gap-1.5">
                                        <FormLabel htmlFor="dataPagamento">Data de Pagamento</FormLabel>
                                        <input
                                            id="dataPagamento"
                                            name="dataPagamento"
                                            type="date"
                                            className={classeCampo("dataPagamento")}
                                            value={formData.dataPagamento}
                                            onChange={handleInputChange}
                                        />
                                        <MensagemErro campo="dataPagamento" />
                                        <span className="text-stone-500 text-xs">
                                            Deixe em branco enquanto a despesa não for paga.
                                        </span>
                                    </div>

                                    <div className="w-full flex flex-col gap-1.5">
                                        <FormLabel htmlFor="formaPagamento">Forma de Pagamento</FormLabel>
                                        <select
                                            id="formaPagamento"
                                            name="formaPagamento"
                                            className={classeCampo("formaPagamento")}
                                            value={formData.formaPagamento}
                                            onChange={handleInputChange}
                                        >
                                            <option value="">Selecione</option>
                                            {FORMAS_PAGAMENTO.map((forma) => (
                                                <option key={forma} value={forma}>{forma}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* VÍNCULOS OPCIONAIS */}
                        <div className="flex flex-col gap-6">
                            <div className="text-stone-600 text-base font-medium">Vínculos (opcionais)</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="w-full flex flex-col gap-1.5">
                                    <FormLabel htmlFor="obraId">Obra</FormLabel>
                                    <select
                                        id="obraId"
                                        name="obraId"
                                        className={classeCampo("obraId")}
                                        value={formData.obraId}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Nenhuma (despesa administrativa)</option>
                                        {obras.map((o) => (
                                            <option key={o.id} value={o.id}>
                                                {o.codigoProjeto} — {o.nomeProjeto}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="w-full flex flex-col gap-1.5">
                                    <FormLabel htmlFor="fornecedorId">Fornecedor</FormLabel>
                                    <select
                                        id="fornecedorId"
                                        name="fornecedorId"
                                        className={classeCampo("fornecedorId")}
                                        value={formData.fornecedorId}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Nenhum</option>
                                        {fornecedores.map((f) => (
                                            <option key={f.id} value={f.id}>{f.nomeFornecedor}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="w-full flex flex-col gap-1.5">
                                <FormLabel htmlFor="observacao">Observação</FormLabel>
                                <textarea
                                    id="observacao"
                                    name="observacao"
                                    rows={3}
                                    className={classeCampo("observacao")}
                                    value={formData.observacao}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>

                        {/* BOTÕES */}
                        <div className="flex items-center w-full gap-4">
                            {isEditMode && podeDeletar && (
                                <button
                                    type="button"
                                    className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                                    onClick={() => setShowConfirmDeleteRegister(true)}
                                >
                                    <img className="w-4 h-4" src="/images/icon/trash-2.png" alt="" />
                                    <span>Apagar</span>
                                </button>
                            )}
                            <div className="flex gap-4 ml-auto">
                                <button
                                    type="button"
                                    className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                                    onClick={handleBack}
                                >
                                    <img className="w-4 h-4" src="/images/icon/arrow-left.png" alt="" />
                                    <span>Voltar</span>
                                </button>
                                <button
                                    type="submit"
                                    disabled={salvando}
                                    className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    <img className="w-4 h-4" src="/images/icon/save.png" alt="" />
                                    <span>{salvando ? "Salvando..." : "Salvar"}</span>
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {/* MODAIS */}
                <ConfirmationModal
                    isVisible={showConfirmDialog}
                    onConfirm={handleConfirm}
                    onCancel={() => setShowConfirmDialog(false)}
                    message="Deseja salvar as informações?"
                />
                <SuccessModal
                    isVisible={showSuccessDialog}
                    message="Informações salvas com sucesso!"
                    onClose={() => setShowSuccessDialog(false)}
                />
                <ConfirmationModal
                    isVisible={showConfirmDeleteRegister}
                    onConfirm={handleDeleteRegister}
                    onCancel={() => setShowConfirmDeleteRegister(false)}
                    message="Deseja apagar esta despesa? Os comprovantes anexados também serão removidos."
                    textButtonConfirm="Sim"
                />
                {showAlertDialog && (
                    <AlertDialog
                        isVisible={showAlertDialog}
                        message={alertMessage}
                        onClose={() => setShowAlertDialog(false)}
                    />
                )}
            </form>
        </Template>
    );
};

export default CadastrarDespesa;
